import Axios from 'axios'
// For debugging capabilities needs to load from dev distribution.
// import { setupCache, buildWebStorage } from 'axios-cache-interceptor/dev'
import { setupCache, buildWebStorage } from 'axios-cache-interceptor'
import { isArray, isEmpty, isNumber, isString } from 'lodash'

const axios = setupCache(Axios, {
  debug: console.log,
  // Cache during 5 minutes.
  ttl: 5 * 60 * 1000,
  // Ignore Drupal API proxy cache headers, preferring to use our custom TTL.
  interpretHeader: false,
  // Store requests cache at session storage.
  storage: buildWebStorage(sessionStorage, 'cache:'),
})

/**
 * @typedef {import('axios').AxiosRequestConfig} AxiosRequestConfig
 * @typedef {import('axios').AxiosResponse} AxiosResponse
 */

/**
 * @typedef {Object} ApiRequestProps
 * @property {string} baseUrl
 * @property {string=} authToken
 * @property {string=} csrfToken
 */

/**
 * @typedef {Object} DoApiRequestProps
 * @property {string} endpoint
 * @property {any} data
 * @property {Object<string, string>} customHeaders
 * @property {string} method
 * @property {Object<string, string|boolean>} extraOptions
 */

/**
 * @typedef {(props: DoApiRequestProps) => Promise<any>} DoApiRequest
 * @typedef {(props: Record<string, any>) => string} PrepareQueryParams
 * @typedef {(key: string) => void} CleanCacheItem
 */

/**
 * @typedef {Object} ApiRequestHook
 * @property {DoApiRequest} doApiRequest
 * @property {PrepareQueryParams} prepareQueryParams
 * @property {CleanCacheItem} cleanCacheItem
 */

/**
 * General API request utility.
 * @param {ApiRequestProps} props
 * @return {ApiRequestHook}
 */
const useApiRequest = ({ baseUrl, authToken, csrfToken }) => {
  const isStorybook = `${process.env.STORYBOOK}` === 'yes'

  /**
   * @typedef {Object} CsrfHeader
   * @property {string=} X-CSRF-Token
   */

  /**
   * Compose a CSRF header for API requests.
   * @return {CsrfHeader}
   */
  const prepareCsrfHeader = () => {
    if (isStorybook) {
      return {}
    }

    return {
      'X-CSRF-Token': csrfToken,
    }
  }

  /**
   * Process a Drupal API endpoint request.
   *
   * @type {DoApiRequest}
   * @param {DoApiRequestProps} props
   * @return {Promise<any>}
   */
  const doApiRequest = async ({ endpoint, data = null, customHeaders, method = '', extraOptions = {} }) => {
    const url = `${baseUrl}${endpoint}`
    const jsonData = data ? JSON.stringify(data) : ''
    // const csrfHeader = prepareCsrfHeader()
    // const authHeader = {
    //   Authorization: `Bearer ${authToken}`,
    // }

    const errorCodes = [
      400, 401, 403, 405, 406, 408, 409, 410, 412, 422, 429, 500, 501, 502, 503, 504,
    ]

    const headers = {
      //'Content-Type': 'application/json',
      'Content-Type': 'application/vnd.api+json',
      Accept: 'application/vnd.api+json',
      // ...(authToken && isStorybook ? authHeader : {}),
      // ...(isEmpty(csrfToken) ? {} : csrfHeader),
      // ...(customHeaders || {}),
    }

    // console.log('DoRequestHead:', headers)

    /**
     * @type {AxiosRequestConfig}
     */
    const options = {
      url,
      method: method || 'GET',
      headers,
      timeout: 5000,
      // ...extraOptions,
    }

    if (data) {
      options.data = jsonData
    } else {
      options.method = method || 'GET'
    }

    // console.log(`doRequestURL: ${url} - ${JSON.stringify(options)}`)
    try {
      /**
       * @type {AxiosResponse}
       */
      const response = await axios(options)
      // If is a success response return data, otherwise null to indicate failure.
      if ([200, 201].includes(response.status)) {
        const contentType = response.headers['content-type']
        if (contentType.includes('json')) {
          return response.data
        } else {
          return {
            data: response.data,
          }
        }
      } else if (errorCodes.includes(response.status)) {
        const errorCode = response.status
        return {
          data: response.data,
          errorCode,
        }
      }
    } catch (error) {
      // console.log('ERROR API request:', error)
      return null
    }
  }

  /**
   * Prepare query string params.
   * @type {PrepareQueryParams}
   * @param {Record<string, any>} params
   * @return {string}
   */
  const prepareQueryParams = (params) => {
    const searchParams = new URLSearchParams()
    Object.entries(params).forEach(([key, value]) => {
      if (isArray(value)) {
        value.forEach((currentValue) => {
          searchParams.append(key, `${currentValue}`)
        })
      } else if (isString(value) || isNumber(value)) {
        searchParams.append(key, `${value}`)
      } else {
        console.error(`Parameter ${key} value type not supported:`, value)
      }
    })

    return searchParams.toString()
  }

  /**
   * This function is used to remove a single item from the cache based on the key
   * @param {string} key
   */
  const cleanCacheItem = async (key) => {
    await axios.storage.remove(key)
  }

  return {
    cleanCacheItem,
    doApiRequest,
    prepareQueryParams,
  }
}

export default useApiRequest
